<template>
  <article class="cost-to-run-breakdown blue-card">
    <div v-for="dataPoint in costToRunData" :key="dataPoint.label" class="cost-to-run-data">
      <p class="cost-to-run-data__label" v-text="dataPoint.label" />
      <p class="cost-to-run-data__value">
        {{ dataPoint.value }}
        <ExplanationModal with-icon :title="runningCostsModalContent.title.value" :description="runningCostsModalContent.description.value" />
      </p>
    </div>
  </article>

  <article class="cost-to-run-breakdown white-card">
    <div class="cost-to-run-data">
      <p class="cost-to-run-data__label" v-text="carDetailsProps.registrationLabel" />

      <p class="cost-to-run-data__value">
        {{ costToRunRegoCost }}
        <ExplanationModal with-icon :title="runningCostsModalContent.title.value" :description="runningCostsModalContent.description.value" />
      </p>
    </div>
  </article>

  <article class="cost-to-run-breakdown white-card">
    <div v-for="dataPoint in costToRunLinks" :key="dataPoint.label" class="cost-to-run-data">
      <p class="cost-to-run-data__label" v-text="dataPoint.label" />
      <p class="cost-to-run-data__value">
        <a :href="dataPoint.hyperlink" class="link" @click="goToLink(dataPoint)" v-text="dataPoint.value" />
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { AnalyticsEventNames, AnalyticsEvents, AnalyticsEventTypes } from '../../foundation/enums/AnalyticsEvents';
import { useCarComparisonStore } from '../../foundation/stores/carComparisonStore';
import ExplanationModal from '../modals/ExplanationModal.vue';
import useRunningCosts from '../../composables/useRunningCosts';
import { ICarDetailsProps, IRegistrationCosts, RegistrationCost } from '../../foundation/interfaces/datasources/IDataSourceProps';
import { useContentStore } from '../../foundation/stores/contentStore';

const props = defineProps<{
  carData: any;
  carDetailsProps: ICarDetailsProps;
  registrationCosts: IRegistrationCosts;
}>();

const route = useRoute();
const carComparisonStore = useCarComparisonStore();

const contentStore = useContentStore();
contentStore.fetchModalDatasources();
const runningCostsModalContent = contentStore.modalDatasources?.modalContentItems?.runningCosts;

const { costToRunData } = useRunningCosts(props.carData, {
  isSearchResult: false,
  costToFillLabel: props.carDetailsProps.costToFillLabel,
  costPer100KmLabel: props.carDetailsProps.costPer100kmLabel,
});

function pushToAnalytics(url: string, isRoadside: boolean) {
  try {
    carComparisonStore.pushToAnalytics(
      route.params.carId as string,
      AnalyticsEvents.NavigationLinkClick,
      isRoadside ? AnalyticsEventNames.RoadsideQuote : AnalyticsEventNames.InsuranceQuote,
      AnalyticsEventTypes.Promo,
      url
    );
  } catch (error) {
    console.log('fail to push to analytics', error);
  }
}

function goToLink(dataPoint: any) {
  const url = dataPoint.hyperlink;
  pushToAnalytics(url, dataPoint.label == 'Roadside assist');
  window.location.href = url;
}

const costToRunLinks = computed(() => {
  return [
    {
      label: props.carDetailsProps.batteriesLabel,
      value: props.carDetailsProps?.batteriesLink?.text,
      hyperlink: props.carDetailsProps?.batteriesLink?.href,
    },
    {
      label: props.carDetailsProps.insuranceLabel,
      value: props.carDetailsProps?.insuranceLink?.text,
      hyperlink: props.carDetailsProps?.insuranceLink?.href,
    },
    {
      label: props.carDetailsProps.roadsideLabel,
      value: props.carDetailsProps?.roadsideLink?.text,
      hyperlink: props.carDetailsProps?.roadsideLink?.href,
    },
  ];
});

const costToRunRegoCost = computed(() => {
  const cylinders: string = props.carData.Combustion_Cylinders || 0;
  const match = props.registrationCosts.registrationCosts.find((item: RegistrationCost) => {
    return Number(item.cylinders) === Number(cylinders);
  });

  return match?.registrationCost ? '$' + match?.registrationCost : 'Data not available';
});
</script>

<style lang="scss" scoped>
.cost-to-run-breakdown {
  margin: 0 0 1rem;
}

.cost-to-run-data {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__value,
  &__label {
    width: 50%;
    color: $racq-dark-grey;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  &__value {
    padding-left: 2rem;
    text-align: right;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  @media screen and (min-width: #{$breakpoint-md}) {
    justify-content: start;

    .cost-to-run-data__value {
      text-align: left;
      display: flex;

      gap: 0.5rem;
    }
  }
}
</style>
